// Ficha pública detallada por tipo de clase.
// URL: /clases/:slug
// Datos: GET /api/clases-tipos/:slug → clase + proximas_sesiones derivadas (14d).

import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Flower2, Wind, Moon, Flame, Bed, Sparkles,
  Clock, ArrowLeft, Calendar, MapPin,
} from 'lucide-react';
import { API_BASE } from '../lib/api';

const ICON_MAP = {
  'flower-2': Flower2,
  'wind': Wind,
  'moon': Moon,
  'flame': Flame,
  'bed': Bed,
  'sparkles': Sparkles,
};

function ClaseIcon({ name, ...rest }) {
  const Comp = ICON_MAP[name] || Sparkles;
  return <Comp {...rest} />;
}

// Formatea YYYY-MM-DD + locale → "Sábado 11 de mayo" (ES) etc.
function formateaFecha(fechaIso, lang) {
  const localeMap = { es: 'es-ES', en: 'en-GB', ru: 'ru-RU', fr: 'fr-FR' };
  const locale = localeMap[lang] || 'es-ES';
  const [y, m, d] = fechaIso.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  const fmt = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long' });
  const s = fmt.format(date);
  return s.charAt(0).toLocaleUpperCase(locale) + s.slice(1);
}

export default function Clase() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setData(null); setError(null);
    fetch(`${API_BASE}/api/clases-tipos/${encodeURIComponent(slug)}?idioma=${lang}`)
      .then((r) => {
        if (!r.ok) {
          return r.json().then((b) => Promise.reject(b));
        }
        return r.json();
      })
      .then((json) => { if (!cancelled) setData(json); })
      .catch((e) => { if (!cancelled) setError(e); });
    return () => { cancelled = true; };
  }, [slug, lang]);

  if (error) {
    return (
      <main className="clase-page">
        <div className="clase-page-inner">
          <button type="button" className="clase-back" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} strokeWidth={1.5} /> {t('comun.volver')}
          </button>
          <div className="clase-page-error">
            {error?.code === 'CLASE_NO_ENCONTRADA'
              ? t('clase_detalle.no_encontrada')
              : t('clases.error_carga')}
          </div>
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="clase-page">
        <div className="clase-page-inner">
          <div className="clase-page-skeleton" />
        </div>
      </main>
    );
  }

  const { clase, proximas_sesiones } = data;
  const tieneDescLarga = !!(clase.descripcion_larga && clase.descripcion_larga.trim());

  return (
    <main className="clase-page">
      <div className="clase-page-inner">
        <button type="button" className="clase-back" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} strokeWidth={1.5} /> {t('comun.volver')}
        </button>

        <header className="clase-page-head">
          <div className="clase-page-icon-wrap">
            <ClaseIcon name={clase.icono_nombre} size={48} strokeWidth={1.2} className="clase-page-icon" />
          </div>
          <div className="clase-page-head-info">
            <span className={'clase-nivel clase-nivel--' + clase.nivel}>
              {t('clases.nivel_' + clase.nivel)}
            </span>
            <h1 className="clase-page-title">{clase.nombre}</h1>
            <div className="clase-page-meta">
              <span className="clase-page-meta-item">
                <Clock size={14} strokeWidth={1.5} /> {clase.duracion_minutos} {t('clase_detalle.minutos')}
              </span>
            </div>
          </div>
        </header>

        <p className="clase-page-resumen">{clase.descripcion}</p>

        <section className="clase-page-cuerpo">
          {tieneDescLarga ? (
            clase.descripcion_larga.split(/\n\n+/).map((parrafo, i) => (
              <p key={i} className="clase-page-parrafo">{parrafo.trim()}</p>
            ))
          ) : (
            <p className="clase-page-placeholder">
              {t('clase_detalle.placeholder_sin_contenido')}
            </p>
          )}
        </section>

        <section className="clase-page-proximas">
          <h2 className="clase-page-sec-title">
            <Calendar size={18} strokeWidth={1.5} /> {t('clase_detalle.proximas_sesiones')}
          </h2>
          {proximas_sesiones && proximas_sesiones.length > 0 ? (
            <ul className="clase-proximas-lista">
              {proximas_sesiones.map((s, i) => (
                <li key={i} className="clase-proxima-item">
                  <div className="clase-proxima-fecha">{formateaFecha(s.fecha, lang)}</div>
                  <div className="clase-proxima-meta">
                    <span><Clock size={12} strokeWidth={1.5} /> {s.hora_inicio}</span>
                    <span className="clase-proxima-formato">
                      <MapPin size={12} strokeWidth={1.5} /> {t('clase_detalle.formato_' + s.formato)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="clase-page-placeholder">{t('clase_detalle.sin_proximas')}</p>
          )}
        </section>

        <div className="clase-page-cta-wrap">
          <Link to="/horarios" className="clase-page-cta">
            {t('clase_detalle.cta_ver_horarios')}
          </Link>
        </div>
      </div>
    </main>
  );
}
